import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Phone,
  Mail,
  ChevronDown,
  Menu,
  X,
  ArrowRight,
  CreditCard,
  Smartphone,
  Globe,
  Monitor,
  Terminal,
  Gift,
  HelpCircle,
  Newspaper,
} from "lucide-react";
import { SITE } from "@/lib/site";
import Logo from "@/components/Logo";

const servicesItems = [
  { to: "/services#credit-card-processing", label: "Card Processing", icon: CreditCard, desc: "Accept all major brands" },
];

const productsItems = [
  { to: "/products", label: "Terminals", icon: Terminal, desc: "Countertop, wireless, smart & pin pads" },
  { to: "/mobile-devices", label: "Mobile Devices", icon: Smartphone, desc: "Sell wherever your business goes" },
  { to: "/e-commerce", label: "E-Commerce", icon: Globe, desc: "Online checkout & gateways" },
  { to: "/pos-systems", label: "POS Systems", icon: Monitor, desc: "Clover, VST POS, entreePOS & RCC123 POS" },
  { to: "/free-pos-program", label: "Free POS Program", icon: Gift, desc: "See if you qualify" },
];

const resourcesItems = [
  { to: "/resources", label: "Blog", icon: Newspaper, desc: "Articles & guides" },
  { to: "/resources/faq", label: "FAQ", icon: HelpCircle, desc: "Common questions" },
];

function DesktopDropdown({ label, items, testId, overlay }) {
  return (
    <div className="relative group">
      <button
        data-testid={testId}
        className={`flex items-center gap-1 text-[16px] font-bold py-2 transition-colors ${overlay ? "text-white hover:text-[var(--swipe-accent)] [text-shadow:0_2px_10px_rgba(6,26,58,0.65)]" : "text-[var(--swipe-navy)] hover:text-[var(--swipe-accent)]"}`}
      >
        {label}
        <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
      </button>
      <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200 absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50">
        <div className="bg-white border border-line rounded-md shadow-2xl p-3 min-w-[360px]">
          <div className="grid gap-1">
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  className="flex items-start gap-3 p-3 rounded-md hover:bg-[var(--swipe-muted)] transition-colors group/item"
                  data-testid={`nav-dropdown-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div className="w-8 h-8 rounded-md bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center shrink-0 group-hover/item:bg-[var(--swipe-accent)] group-hover/item:text-white transition-colors">
                    <Icon className="w-4 h-4" strokeWidth={2.25} />
                  </div>
                  <div>
                    <div className="text-[13.5px] font-semibold text-[var(--swipe-navy)]">{item.label}</div>
                    <div className="text-[11.5px] text-slate-500 mt-0.5">{item.desc}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileAccordion({ label, items, onNavigate }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-line">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left font-semibold text-[var(--swipe-navy)]"
        data-testid={`mobile-nav-toggle-${label.toLowerCase()}`}
      >
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pb-3 grid gap-1">
          {items.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              onClick={onNavigate}
              className="px-3 py-2 text-sm text-slate-600 rounded-md hover:bg-[var(--swipe-muted)]"
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

const navLinkClass = (isOverlay) => ({ isActive }) =>
  `text-[16px] font-bold py-2 relative transition-colors after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-[2px] after:bg-[var(--swipe-accent)] after:scale-x-0 hover:after:scale-x-100 after:origin-left after:transition-transform after:duration-200 ${
    isActive
      ? "text-[var(--swipe-accent)] after:scale-x-100"
      : isOverlay
        ? "text-white hover:text-[var(--swipe-accent)] [text-shadow:0_2px_10px_rgba(6,26,58,0.55)]"
        : "text-[var(--swipe-navy)] hover:text-[var(--swipe-accent)]"
  }`;

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const closeMobile = () => setMobileOpen(false);

  // Overlay mode on home page only
  const overlay = isHome && !mobileOpen;

  return (
    <header className={`${overlay ? "absolute" : "sticky"} top-0 left-0 right-0 z-50`}>
      {/* Navy gradient strip — only on overlay, makes logo + nav readable without boxing anything */}
      {overlay && (
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-full pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(6,26,58,0.65) 0%, rgba(6,26,58,0.42) 55%, rgba(6,26,58,0) 100%)",
          }}
        />
      )}
      {/* Top utility bar — phone + email aligned right */}
      <div className={`relative ${overlay ? "bg-transparent" : "bg-[var(--swipe-navy)]"} text-[13px]`}>
        <div className={`container-swipe flex items-center justify-end py-2.5 gap-6 ${overlay ? "border-b border-white/10" : ""}`}>
          <a
            href={`tel:${SITE.phoneRaw}`}
            className={`flex items-center gap-2 transition-colors ${overlay ? "text-white hover:text-[var(--swipe-accent)] [text-shadow:0_1px_6px_rgba(6,26,58,0.5)]" : "text-white/85 hover:text-[var(--swipe-accent)]"}`}
            data-testid="topbar-phone"
          >
            <Phone className="w-3.5 h-3.5" />
            {SITE.phone}
          </a>
          <a
            href={`mailto:${SITE.email}`}
            className={`hidden sm:flex items-center gap-2 transition-colors ${overlay ? "text-white hover:text-[var(--swipe-accent)] [text-shadow:0_1px_6px_rgba(6,26,58,0.5)]" : "text-white/85 hover:text-[var(--swipe-accent)]"}`}
            data-testid="topbar-email"
          >
            <Mail className="w-3.5 h-3.5" />
            {SITE.email}
          </a>
        </div>
      </div>

      {/* Main nav: logo left, nav center-right, New Application CTA right */}
      <div className={`relative ${overlay ? "bg-transparent" : "bg-white border-b border-line"}`}>
        <div className="container-swipe flex items-center justify-between py-5 md:py-6 gap-6">
          <Logo variant={overlay ? "light" : "dark"} className="w-[200px] sm:w-[240px] md:w-[300px] h-auto" linkClassName={overlay ? "drop-shadow-[0_3px_14px_rgba(6,26,58,0.6)]" : ""} testId="logo-link" />

          <nav className="hidden xl:flex items-center gap-8 ml-auto">
            <DesktopDropdown label="Services" items={servicesItems} testId="nav-services" overlay={overlay} />
            <DesktopDropdown label="Products" items={productsItems} testId="nav-products" overlay={overlay} />
            <NavLink to="/partnerships" className={navLinkClass(overlay)} data-testid="nav-partnerships">Partnerships</NavLink>
            <DesktopDropdown label="Resources" items={resourcesItems} testId="nav-resources" overlay={overlay} />
            <NavLink to="/contact" className={navLinkClass(overlay)} data-testid="nav-contact">Contact</NavLink>
            <NavLink to="/about" className={navLinkClass(overlay)} data-testid="nav-about">About Us</NavLink>
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            <Link
              to="/get-started"
              className="hidden md:inline-flex btn-primary-swipe text-[15px] font-extrabold py-3.5 px-6 tracking-wide"
              data-testid="header-cta-new-application"
            >
              New Application
              <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              className={`xl:hidden p-2.5 rounded-md border ${overlay ? "border-white/30 text-white" : "border-line text-[var(--swipe-navy)]"}`}
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="xl:hidden border-t border-line bg-white">
            <div className="container-swipe py-4">
              <MobileAccordion label="Services" items={servicesItems} onNavigate={closeMobile} />
              <MobileAccordion label="Products" items={productsItems} onNavigate={closeMobile} />
              <Link to="/partnerships" onClick={closeMobile} className="block py-4 font-semibold text-[var(--swipe-navy)] border-b border-line">Partnerships</Link>
              <MobileAccordion label="Resources" items={resourcesItems} onNavigate={closeMobile} />
              <Link to="/contact" onClick={closeMobile} className="block py-4 font-semibold text-[var(--swipe-navy)] border-b border-line">Contact</Link>
              <Link to="/about" onClick={closeMobile} className="block py-4 font-semibold text-[var(--swipe-navy)] border-b border-line">About Us</Link>
              <Link
                to="/get-started"
                onClick={closeMobile}
                className="btn-primary-swipe w-full justify-center mt-5"
                data-testid="mobile-cta-new-application"
              >
                New Application <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
